import type { Doc } from "building-types/doc";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type {
  DocEntityHandle,
  GroupableEntityHandle,
  GroupableEntityType,
} from "building-types/handles/docEntityHandleTypes";
import { isSystemContentTemplateId } from "../contentTemplateTools";
import { docEntityIsInRoomLayout } from "../docRoomLayouts";
import docEntityGetParent from "./docEntityGetParent";
import { docGetTableEntity } from "./docGetTableEntity";

export type GroupableEntity<T extends GroupableEntityType = GroupableEntityType> = DocEntityBase<T>;

export function isGroupableEntityHandle(handle: DocEntityHandle): handle is GroupableEntityHandle {
  return docEntityTypeIsGroupable(handle?.type);
}

export function docEntityTypeIsGroupable(entityType: any): entityType is GroupableEntityType {
  return entityType in groupableEntityTypes;
}

export function docEntityIsGroupable(doc: Doc, handle: DocEntityHandle): handle is GroupableEntityHandle {
  if (docEntityIsInRoomLayout(doc, handle)) {
    return false;
  }

  if (handle.type === "CONTENT_TEMPLATE" && isSystemContentTemplateId(handle.id)) {
    return false;
  }

  if (handle.type === "CONTENT_TEMPLATE_INSTANCE") {
    // Only model hosted drawing instances are groupable
    const entity = docGetTableEntity(doc, handle);
    if (entity?.templateType === "SHEET") {
      return false;
    }
    const parent = docEntityGetParent(doc, handle);
    if (parent?.type === "CONTENT_TEMPLATE" || parent?.type === "CONTENT_TEMPLATE_INSTANCE") {
      return false;
    }
  }

  return docEntityTypeIsGroupable(handle?.type);
}

export const groupableEntityTypes: {
  [key in GroupableEntityType]: boolean;
} = {
  ROOF_SYSTEM: true,
  ROOF: true,
  ROOF_VOLUME: true,
  FACADE: true,
  SETTINGS_SET: true,
  BLOCK: true,
  MODIFIER: true,
  MEASUREMENT: true,
  MASONRY_COMPONENT: true,
  OPENING: true,
  REFERENCE_GEOM: true,
  ANNOTATION: true,
  CASEWORK: true,
  RAILING: true,
  SITE: true,
  FLOOR: true,
  FLOOR_FINISH: true,
  COLUMN: true,
  STAIR_SYSTEM: true,
  STAIR_BLOCK: true,
  PROP_INSTANCE: true,
  SHEET: true,
  CEILING: true,
  ELECTRICAL_LAYOUT: true,
  ELECTRICAL_DEVICE: true,
  PALETTE: true,
  CONTENT_TEMPLATE: true,
  CONTENT_TEMPLATE_INSTANCE: true,
  DATUM: true,
  DATA_SEQUENCE: true,
  PROP: true,
  FRAMING_COMPONENT: true,
  FOUNDATION_ASSEMBLY: true,
  FOUNDATION_COMPONENT: true,
  HEADER_ASSEMBLY: true,
  WALL_PLANE_ASSEMBLY: true,
  FRAME_FLOOR_ASSEMBLY: true,
  SLAB_FLOOR_ASSEMBLY: true,
  SLAB_COMPONENT: true,
  SHEATHING_COMPONENT: true,
  GLOBAL_PARAMETER: true,
  SYSTEM_PARAMETER: true,
};
/**
 * Certain groupable entity types are unique in that they are groupable
 * and/or distinguished by their sub-types. The discriminating sub-type fields
 * are often unique to the entity type, so require special handling.
 */

export type GroupableEntityBySubType = Extract<GroupableEntityType, "CONTENT_TEMPLATE" | "SETTINGS_SET" | "DATUM">;

export function isGroupableEntityBySubType(entityType: GroupableEntityType): entityType is GroupableEntityBySubType {
  return entityType === "CONTENT_TEMPLATE" || entityType === "SETTINGS_SET" || entityType === "DATUM";
}
